import React, { useState, useEffect } from 'react';
import { ControlledTreeEnvironment, Tree } from 'react-complex-tree';
import 'react-complex-tree/lib/style.css';
import { withRouter } from 'react-router-dom';
import { withErrorBoundary, EventEmitter, Loader } from '@nile/components';
import { transformDataToTree } from '../helper';
import styled from 'styled-components';
import CollectionTitle from './collectionTitle';
import { useKbContext } from '../../contexts/KbContext';
import { isEmpty } from 'lodash';
import {
  KB_CONSTANTS,
  KB_ROUTE,
  COLLECTION_TREE_DEFAULT_CONDITIONS
} from '@nile/rmm/src/modules/knowledge-base';
import {
  GET_KB_ITEMS_LIST,
  GET_COLLECTIONS,
  GET_KB_ITEM
} from '@nile/rmm/src/modules/knowledge-base/graphql';
import { useLazyQuery } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import { KB_ROUTE_ID } from '../../constants/kb-constants';

const TreeContainer = styled.div`
  .rct-tree-items-container,
  .rct-tree-root {
    width: 100%;
    outline: none !important;
    background: transparent;
  }
`;

const DEFAULT_SORT = [
  {
    attribute: 'name',
    order: 'ASC'
  }
];

const KbCollectionTree = ({ history, match }) => {
  const {
    expandedItems,
    setExpandedItems,
    treeItems,
    setTreeItems,
    setActiveViewConfig,
    activeViewConfig
  } = useKbContext();
  const [selectedItems, setSelectedItems] = useState([]);
  const [focusedItem, setFocusedItem] = useState(null);
  const [loadingItems, setLoadingItems] = useState([]);
  const [defaultCollection, setDefaultCollection] = useState(null);
  const { pagename: collectionId } = match?.params;

  const [fetchCollections, { loading: collectionLoading }] = useLazyQuery(
    GET_COLLECTIONS,
    {
      client: AccountClient,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data?.getKbItems?.items) {
          setDefaultCollection(data?.getKbItems?.items[0]);
          const transformedData = transformDataToTree(data.getKbItems.items);
          setTreeItems(transformedData);
        }
      }
    }
  );

  const [fetchChildrenQuery] = useLazyQuery(GET_COLLECTIONS, {
    client: AccountClient,
    fetchPolicy: 'network-only'
  });

  const [fetchCollectionQuery] = useLazyQuery(GET_KB_ITEM, {
    client: AccountClient,
    fetchPolicy: 'network-only'
  });

  const fetchCollectionFromId = async (collectionId, fetchChildren = false) => {
    if (fetchChildren) {
      // Use GET_KB_ITEMS_LIST to fetch children
      const { data, error } = await fetchChildrenQuery({
        variables: {
          listInfo: {
            sort: DEFAULT_SORT,
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'parent.itemId',
                  operator: 'is',
                  value: collectionId
                },
                {
                  attribute: 'itemType',
                  operator: 'is',
                  value: KB_CONSTANTS.KB_COLLECTION
                }
              ]
            }
          }
        }
      });
      if (error) throw error;
      return data?.getKbItems?.items || [];
    } else {
      if (!collectionId || Object.keys?.(KB_ROUTE_ID)?.includes(collectionId))
        return [];
      // Use GET_KB_ITEM to fetch collection details
      const { data, error } = await fetchCollectionQuery({
        variables: {
          input: {
            itemId: collectionId
          }
        }
      });
      if (error) throw error;
      return data?.getKbItem ? [data?.getKbItem] : [];
    }
  };

  useEffect(() => {
    if (isEmpty(treeItems)) {
      fetchCollections({
        variables: {
          listInfo: {
            sort: DEFAULT_SORT,
            condition: COLLECTION_TREE_DEFAULT_CONDITIONS
          }
        }
      });
    }
  }, []);

  const refetchCollection = async () => {
    try {
      await fetchCollections({
        variables: {
          listInfo: {
            sort: DEFAULT_SORT,
            condition: COLLECTION_TREE_DEFAULT_CONDITIONS
          }
        }
      });
    } catch (error) {
      console.error('Error refetching collections:', error);
    }
  };

  useEffect(() => {
    if (isEmpty(treeItems)) return;
    if (!match?.url?.includes(KB_ROUTE.COLLECTION_VIEW)) {
      setSelectedItems([]);
      return;
    }
    if (!collectionId) return;
    if (treeItems[collectionId]) {
      // if (selectedItems.includes(collectionId)) return; // on rename not updating toolbar
      setSelectedItems([collectionId]);
      setActiveViewConfig({
        source: KB_CONSTANTS.SOURCE_COLLECTION_TREE,
        data: treeItems[collectionId]?.fullData,
        activeItemId: collectionId
      });
      if (!selectedItems.includes(collectionId)) {
        expandParent(collectionId);
      }
      return;
    }
    selectFolderBasedOnRoute(collectionId);
  }, [collectionId, treeItems, match?.url]);

  const selectFolderBasedOnRoute = async (folderId) => {
    if (
      !folderId ||
      selectedItems.includes(folderId) ||
      expandedItems.includes(folderId)
    )
      return;
    try {
      const findCollection = await fetchCollectionFromId(folderId, false);
      if (findCollection?.length) {
        let parentId = findCollection[0]?.parent?.itemId;
        expandParent(parentId);
      }
    } catch (error) {
      if (defaultCollection?.itemId) {
        handleSelect([defaultCollection.itemId]);
      }
      console.error('Error fetching collection from Id');
    }
  };

  const expandParent = async (folderId) => {
    const parentId = treeItems[folderId]?.parent?.id;
    if (!parentId) {
      if (!expandedItems.includes(folderId))
        setExpandedItems((prev) => [...prev, folderId]);
      if (!treeItems[folderId]?.children?.length) {
        setLoadingItems((prev) => [...prev, folderId]);
        try {
          const newChildrenData = await fetchCollectionFromId(folderId, true);
          if (newChildrenData.length > 0) {
            setTreeItems((prev) => transformDataToTree(newChildrenData, prev));
          }
        } catch (error) {
          console.error('Error fetching Collection:', error);
        } finally {
          setLoadingItems((prev) => prev.filter((id) => id !== folderId));
        }
      }
    } else {
      if (!expandedItems.includes(parentId)) {
        setExpandedItems((prev) => [...prev, parentId]);
        if (!treeItems[parentId]?.children?.length) {
          setLoadingItems((prev) => [...prev, parentId]);
          try {
            const newChildrenData = await fetchCollectionFromId(parentId);
            if (newChildrenData.length > 0) {
              setTreeItems((prev) =>
                transformDataToTree(newChildrenData, prev)
              );
            }
          } catch (error) {
            console.error('Error fetching Collection:', error);
          } finally {
            setLoadingItems((prev) => prev.filter((id) => id !== parentId));
          }
        }
      }
    }
  };

  const calculateDepth = (itemId) => {
    let depth = 0;
    let currentItem = treeItems[itemId];

    while (currentItem?.parent) {
      depth++;
      currentItem = treeItems[currentItem.parent.id];
    }

    return depth;
  };

  const handleExpand = async (item) => {
    if (!item) return;
    if (
      !treeItems[item.index]?.children?.length &&
      treeItems[item.index]?.parent === null
    ) {
      setLoadingItems((prev) => [...prev, item.index]);
      const newChildrenData = await fetchCollectionFromId(item.index, true);
      setTreeItems((prev) => transformDataToTree(newChildrenData, prev));
    }
    setLoadingItems((prev) => prev.filter((id) => id !== item.index));
    setExpandedItems((prev) => {
      if (!prev.includes(item.index)) {
        return [...prev, item.index];
      }
      return prev;
    });
  };

  const handleSelect = (items) => {
    if (items.length > 0 && selectedItems[0] !== items[0]) {
      history.push(`${KB_ROUTE.COLLECTION_VIEW}/${items[0]}`);
    }
  };

  const handleRefresh = () => {
    refetchCollection();
  };

  useEffect(() => {
    EventEmitter.subscribe(
      KB_CONSTANTS.COL_TREE_REFETCH_EVENT_EMITTER,
      refetchCollection
    );
    return () => {
      EventEmitter.unsubscribe(KB_CONSTANTS.COL_TREE_REFETCH_EVENT_EMITTER);
    };
  }, []);

  if (collectionLoading) {
    return (
      <div className="mt20">
        <Loader />
      </div>
    );
  }

  if (!treeItems) return null;

  return (
    <TreeContainer>
      <ControlledTreeEnvironment
        items={treeItems}
        renderDepthOffset={KB_CONSTANTS.COLLECTION_TREE_DEPTH_OFFSET}
        getItemTitle={(item) => item.data}
        viewState={{
          ['kb-collection-tree']: {
            focusedItem,
            expandedItems,
            selectedItems,
            loadingItems
          }
        }}
        renderItem={(props) => (
          <CollectionTitle
            {...props}
            expandedItems={expandedItems}
            selectedItems={selectedItems}
            focusedItem={focusedItem}
            handleSelect={handleSelect}
            handleExpand={handleExpand}
            setExpandedItems={setExpandedItems}
            loadingItems={loadingItems}
          />
        )}
        // canSearchByStartingTyping
      >
        <Tree treeId="kb-collection-tree" rootItem="root" treeLabel="KB Tree" />
      </ControlledTreeEnvironment>
    </TreeContainer>
  );
};

export default withErrorBoundary(withRouter(KbCollectionTree));
